import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'
import {
  filterRegistrations, resolveVariables, getVariableWarnings, KNOWN_VARIABLES,
} from '../../supabase/functions/_shared/notification-logic.ts'

// Not exported: eslint-plugin-react-refresh flags non-component exports
const INPUT_CLS =
  'w-full rounded-lg border border-outline-variant px-3 py-2.5 text-sm ' +
  'focus:border-primary focus:outline-none focus:ring-2 focus:ring-oasis-200 transition bg-surface-container-lowest'

export default function NotificationsTab({ user, showToast }) {
  const { t } = useTranslation()
  const [view, setView] = useState('compose')
  const [camps, setCamps] = useState([])
  const [templates, setTemplates] = useState([])
  const [notifications, setNotifications] = useState([])
  const [draft, setDraft] = useState(null) // template loaded via "Use"

  const reloadTemplates = useCallback(async () => {
    const { data } = await supabase
      .from('notification_templates').select('*').order('created_at', { ascending: false })
    setTemplates(data ?? [])
  }, [])

  const reloadNotifications = useCallback(async () => {
    const { data } = await supabase
      .from('notifications')
      .select('*, camps(name)')
      .order('created_at', { ascending: false })
    setNotifications(data ?? [])
  }, [])

  useEffect(() => {
    supabase.from('camps').select('*').order('start_date', { ascending: false })
      .then(({ data }) => setCamps(data ?? []))
    // eslint-disable-next-line react-hooks/set-state-in-effect
    reloadTemplates()
    reloadNotifications()
  }, [reloadTemplates, reloadNotifications])

  const views = [
    ['compose', t('notifications.viewCompose')],
    ['templates', t('notifications.viewTemplates')],
    ['history', t('notifications.viewHistory')],
  ]

  return (
    <div>
      <div className="mb-6 flex gap-2">
        {views.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setView(key)}
            className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
              view === key ? 'bg-primary text-white' : 'bg-surface-container-lowest border border-outline-variant text-on-surface-variant hover:bg-surface-container-low'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {view === 'compose' && (
        <ComposeView
          t={t} user={user} camps={camps} templates={templates} draft={draft}
          showToast={showToast} reloadTemplates={reloadTemplates}
          onSent={() => { reloadNotifications(); setView('history') }}
        />
      )}
      {view === 'templates' && (
        <TemplatesView
          t={t} templates={templates} reloadTemplates={reloadTemplates} showToast={showToast}
          onUse={tpl => { setDraft({ ...tpl, loadedAt: Date.now() }); setView('compose') }}
        />
      )}
      {view === 'history' && (
        <HistoryView
          t={t} notifications={notifications}
          reloadNotifications={reloadNotifications} showToast={showToast}
        />
      )}
    </div>
  )
}

// Keep in sync with renderBodyHtml in supabase/functions/send-notification/index.ts
function escapeHtml(s) {
  return s.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
}
function renderBodyHtml(rawBody, ctx) {
  return resolveVariables(escapeHtml(rawBody), ctx, 'html').replaceAll('\n', '<br>')
}

const PAYMENT_STATUSES = ['Paid', 'Partial Payment', 'Confirmed', 'Pending', 'Sponsored']

function ComposeView({ t, user, camps, draft, showToast, onSent, reloadTemplates }) {
  const [campId, setCampId] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [filters, setFilters] = useState({
    payment_status: [], needs_transport: null, room_ids: [], sex: null, min_age: '', max_age: '',
  })
  const [camp, setCamp] = useState(null)
  const [regs, setRegs] = useState([])
  const [rooms, setRooms] = useState([])
  const [roomIdByRegistration, setRoomIdByRegistration] = useState({})
  const [previewed, setPreviewed] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewIdx, setPreviewIdx] = useState(0)
  const [scheduleAt, setScheduleAt] = useState('')
  const [saveTemplate, setSaveTemplate] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const [busy, setBusy] = useState(false)
  const bodyRef = useRef(null)

  // Load a template selected in the Templates view
  useEffect(() => {
    if (!draft) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSubject(draft.subject)
    setBody(draft.body)
    if (draft.camp_id) setCampId(draft.camp_id)
  }, [draft])

  // Any edit invalidates the previous preview
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { setPreviewed(false) }, [campId, subject, body, filters])

  // Load camp data when the camp changes
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!campId) { setCamp(null); setRegs([]); setRooms([]); setRoomIdByRegistration({}); return }
    let stale = false
    async function load() {
      const [{ data: campData }, { data: regData }, { data: roomData }] = await Promise.all([
        supabase.from('camps').select('*').eq('id', campId).single(),
        supabase.from('registrations')
          .select('id, user_id, full_name, payment_status, needs_transport, sex, date_of_birth, amount_owed, amount_paid')
          .eq('camp_id', campId),
        supabase.from('rooms').select('id, name, photo_url').eq('camp_id', campId).order('name'),
      ])
      const roomIds = (roomData ?? []).map(r => r.id)
      const { data: assignments } = roomIds.length > 0
        ? await supabase.from('room_assignments').select('registration_id, room_id').in('room_id', roomIds)
        : { data: [] }
      if (stale) return
      setCamp(campData)
      setRegs(regData ?? [])
      setRooms(roomData ?? [])
      setRoomIdByRegistration(Object.fromEntries((assignments ?? []).map(a => [a.registration_id, a.room_id])))
    }
    load()
    return () => { stale = true }
  }, [campId])

  const normalizedFilters = useMemo(() => ({
    payment_status: filters.payment_status.length > 0 ? filters.payment_status : null,
    needs_transport: filters.needs_transport,
    room_ids: filters.room_ids.length > 0 ? filters.room_ids : null,
    sex: filters.sex,
    min_age: filters.min_age === '' ? null : parseInt(filters.min_age),
    max_age: filters.max_age === '' ? null : parseInt(filters.max_age),
  }), [filters])

  const audience = useMemo(
    () => filterRegistrations(regs, normalizedFilters, roomIdByRegistration),
    [regs, normalizedFilters, roomIdByRegistration],
  )

  const roomById = useMemo(() => new Map(rooms.map(r => [r.id, r])), [rooms])

  // Mirrors buildCtx in the edge function
  const buildCtx = useCallback(reg => {
    const roomId = roomIdByRegistration[reg.id]
    const room = roomId ? roomById.get(roomId) ?? null : null
    return {
      registration: {
        full_name: reg.full_name, needs_transport: reg.needs_transport,
        amount_owed: reg.amount_owed, amount_paid: reg.amount_paid,
      },
      camp,
      room: room ? { name: room.name, photo_url: room.photo_url } : null,
    }
  }, [roomIdByRegistration, roomById, camp])

  const warnings = useMemo(() => {
    if (!camp || audience.length === 0) return []
    return getVariableWarnings(`${subject}\n${body}`, audience.map(buildCtx))
  }, [camp, audience, subject, body, buildCtx])

  const formReady = campId && subject.trim() && body.trim() && audience.length > 0

  function insertVariable(name) {
    const token = `{{${name}}}`
    const el = bodyRef.current
    const start = el?.selectionStart ?? body.length
    const end = el?.selectionEnd ?? start
    setBody(body.slice(0, start) + token + body.slice(end))
    requestAnimationFrame(() => {
      el?.focus()
      el?.setSelectionRange(start + token.length, start + token.length)
    })
  }

  function toggleInList(key, value) {
    setFilters(p => ({
      ...p,
      [key]: p[key].includes(value) ? p[key].filter(v => v !== value) : [...p[key], value],
    }))
  }

  async function maybeSaveTemplate() {
    if (!saveTemplate || !templateName.trim()) return
    const { error } = await supabase.from('notification_templates').insert({
      camp_id: campId, name: templateName.trim(), subject, body, created_by: user.id,
    })
    if (error) showToast(error.message, 'error')
    else { showToast(t('notifications.toastTemplateSaved'), 'success'); reloadTemplates() }
  }

  async function sendTest() {
    setBusy(true)
    const { data, error } = await supabase.functions.invoke('send-notification', {
      body: { test: true, draft: { camp_id: campId, subject, body, filters: normalizedFilters }, testEmail: user.email },
    })
    setBusy(false)
    if (error || data?.error) showToast(error?.message ?? data.error, 'error')
    else showToast(t('notifications.toastTestSent'), 'success')
  }

  async function send(scheduledAtIso) {
    if (!previewed) { showToast(t('notifications.previewRequired'), 'error'); return }
    if (!scheduledAtIso && !window.confirm(t('notifications.confirmSend', { count: audience.length }))) return
    setBusy(true)
    const { data: row, error } = await supabase.from('notifications').insert({
      camp_id: campId, subject, body, filters: normalizedFilters,
      status: 'scheduled', scheduled_at: scheduledAtIso, created_by: user.id,
    }).select().single()
    if (error) { setBusy(false); showToast(error.message, 'error'); return }

    await maybeSaveTemplate()

    if (!scheduledAtIso) {
      const { data, error: invokeError } = await supabase.functions.invoke('send-notification', {
        body: { notificationId: row.id },
      })
      setBusy(false)
      if (invokeError || data?.error) { showToast(invokeError?.message ?? data.error, 'error'); return }
      showToast(t('notifications.toastSent'), 'success')
    } else {
      setBusy(false)
      showToast(t('notifications.toastScheduled'), 'success')
    }
    onSent()
  }

  const previewCtxs = audience.slice(0, 3).map(buildCtx)
  const previewCtx = previewCtxs[previewIdx]

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Camp */}
      <div>
        <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('notifications.labelCamp')}</label>
        <select value={campId} onChange={e => setCampId(e.target.value)} className={INPUT_CLS}>
          <option value="">{t('notifications.selectCampPlaceholder')}</option>
          {camps.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {/* Subject + body */}
      <div>
        <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('notifications.labelSubject')}</label>
        <input type="text" value={subject} onChange={e => setSubject(e.target.value)} className={INPUT_CLS} />
      </div>
      <div>
        <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('notifications.labelBody')}</label>
        <textarea
          ref={bodyRef} rows={10} value={body} onChange={e => setBody(e.target.value)}
          className="w-full rounded-lg border border-outline-variant px-3 py-2.5 text-sm font-mono focus:border-primary focus:outline-none focus:ring-2 focus:ring-oasis-200 transition"
        />
        <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-on-surface-variant">{t('notifications.insertVariable')}</p>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {KNOWN_VARIABLES.map(v => (
            <button key={v} type="button" onClick={() => insertVariable(v)}
              className="rounded-full border border-oasis-300 bg-oasis-50 px-2.5 py-1 text-xs font-mono text-oasis-800 hover:bg-oasis-100 transition">
              {`{{${v}}}`}
            </button>
          ))}
        </div>
      </div>

      {/* Audience filters */}
      <div className="rounded-2xl border border-outline-variant/60 bg-surface-container-lowest p-5 space-y-4">
        <h4 className="text-sm font-bold text-on-surface">{t('notifications.audienceTitle')}</h4>
        <div>
          <p className="text-sm font-medium text-on-surface-variant mb-1.5">{t('notifications.filterPaymentStatus')}</p>
          <div className="flex flex-wrap gap-3">
            {PAYMENT_STATUSES.map(s => (
              <label key={s} className="flex items-center gap-1.5 text-sm text-on-surface-variant">
                <input type="checkbox" checked={filters.payment_status.includes(s)}
                  onChange={() => toggleInList('payment_status', s)} />
                {s}
              </label>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('notifications.filterTransport')}</label>
            <select
              value={filters.needs_transport === null ? '' : String(filters.needs_transport)}
              onChange={e => setFilters(p => ({ ...p, needs_transport: e.target.value === '' ? null : e.target.value === 'true' }))}
              className={INPUT_CLS}
            >
              <option value="">{t('notifications.transportAny')}</option>
              <option value="true">{t('notifications.transportYes')}</option>
              <option value="false">{t('notifications.transportNo')}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('notifications.filterSex')}</label>
            <select value={filters.sex ?? ''}
              onChange={e => setFilters(p => ({ ...p, sex: e.target.value || null }))} className={INPUT_CLS}>
              <option value="">{t('notifications.sexAny')}</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          </div>
        </div>
        {rooms.length > 0 && (
          <div>
            <p className="text-sm font-medium text-on-surface-variant mb-1.5">{t('notifications.filterRooms')}</p>
            <div className="flex flex-wrap gap-3">
              {rooms.map(r => (
                <label key={r.id} className="flex items-center gap-1.5 text-sm text-on-surface-variant">
                  <input type="checkbox" checked={filters.room_ids.includes(r.id)}
                    onChange={() => toggleInList('room_ids', r.id)} />
                  {r.name}
                </label>
              ))}
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('notifications.filterMinAge')}</label>
            <input type="number" min="0" value={filters.min_age}
              onChange={e => setFilters(p => ({ ...p, min_age: e.target.value }))} className={INPUT_CLS} />
          </div>
          <div>
            <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('notifications.filterMaxAge')}</label>
            <input type="number" min="0" value={filters.max_age}
              onChange={e => setFilters(p => ({ ...p, max_age: e.target.value }))} className={INPUT_CLS} />
          </div>
        </div>
        <p className="text-sm font-semibold text-oasis-800">
          {campId ? t('notifications.recipientCount', { count: audience.length }) : ''}
        </p>
      </div>

      {/* Save as template */}
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-sm font-medium text-on-surface-variant">
          <input type="checkbox" checked={saveTemplate} onChange={e => setSaveTemplate(e.target.checked)} />
          {t('notifications.saveAsTemplate')}
        </label>
        {saveTemplate && (
          <input type="text" placeholder={t('notifications.templateName')} value={templateName}
            onChange={e => setTemplateName(e.target.value)} className={INPUT_CLS + ' max-w-xs'} />
        )}
      </div>

      {/* Schedule + actions */}
      <div className="flex flex-wrap items-end gap-3">
        <button type="button" disabled={!formReady || busy}
          onClick={() => { setPreviewIdx(0); setPreviewOpen(true); setPreviewed(true) }}
          className="rounded-xl border border-primary px-5 py-2.5 text-sm font-semibold text-oasis-800 hover:bg-oasis-50 disabled:opacity-60 disabled:cursor-not-allowed transition">
          {t('notifications.previewBtn')}
        </button>
        <button type="button" disabled={!formReady || busy} onClick={sendTest}
          className="rounded-xl border border-outline-variant px-5 py-2.5 text-sm font-medium text-on-surface-variant hover:bg-surface-container-low disabled:opacity-60 disabled:cursor-not-allowed transition">
          {t('notifications.sendTestBtn')}
        </button>
        <button type="button" disabled={!formReady || busy || !previewed} onClick={() => send(null)}
          className="rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-oasis-800 disabled:opacity-60 disabled:cursor-not-allowed transition">
          {busy ? t('notifications.sendingBtn') : t('notifications.sendNowBtn')}
        </button>
        <div>
          <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('notifications.scheduleFor')}</label>
          <div className="flex gap-2">
            <input type="datetime-local" value={scheduleAt}
              onChange={e => setScheduleAt(e.target.value)} className={INPUT_CLS} />
            <button type="button" disabled={!formReady || busy || !previewed || !scheduleAt}
              onClick={() => send(new Date(scheduleAt).toISOString())}
              className="rounded-xl border border-primary px-5 py-2.5 text-sm font-semibold text-oasis-800 hover:bg-oasis-50 disabled:opacity-60 disabled:cursor-not-allowed transition whitespace-nowrap">
              {t('notifications.scheduleBtn')}
            </button>
          </div>
        </div>
      </div>
      {campId && audience.length === 0 && (
        <p className="text-sm text-red-600">{t('notifications.noRecipients')}</p>
      )}

      {/* Preview modal */}
      {previewOpen && previewCtx && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#2c241c]/50 p-4"
          onClick={e => { if (e.target === e.currentTarget) setPreviewOpen(false) }}>
          <div className="w-full max-w-2xl bg-surface-container-lowest rounded-2xl shadow-xl border border-outline-variant/60 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl font-semibold tracking-tight text-on-surface">{t('notifications.previewTitle')}</h3>
              <button onClick={() => setPreviewOpen(false)} className="text-on-surface-variant/70 hover:text-on-surface-variant text-2xl leading-none">×</button>
            </div>
            {warnings.length > 0 && (
              <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-1">
                {warnings.map(w => (
                  <p key={w.variable} className="text-sm text-amber-800">
                    {t('notifications.warningMissing', { count: w.missingCount, variable: w.variable })}
                  </p>
                ))}
              </div>
            )}
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm text-on-surface-variant">
                {t('notifications.previewNav', { index: previewIdx + 1, total: previewCtxs.length })}
              </p>
              <div className="flex gap-2">
                <button disabled={previewIdx === 0} onClick={() => setPreviewIdx(i => i - 1)}
                  className="rounded-lg border border-outline-variant px-3 py-1 text-sm disabled:opacity-40">‹</button>
                <button disabled={previewIdx >= previewCtxs.length - 1} onClick={() => setPreviewIdx(i => i + 1)}
                  className="rounded-lg border border-outline-variant px-3 py-1 text-sm disabled:opacity-40">›</button>
              </div>
            </div>
            <p className="mb-2 text-sm font-bold text-on-surface">{resolveVariables(subject, previewCtx, 'text')}</p>
            <div className="rounded-xl border border-outline-variant/60 bg-[#f3ecdd] p-4 text-sm text-[#2c241c]"
              dangerouslySetInnerHTML={{ __html: renderBodyHtml(body, previewCtx) }} />
          </div>
        </div>
      )}
    </div>
  )
}

// Implemented in Task 10
function TemplatesView({ t, templates, reloadTemplates, onUse, showToast }) {
  const [editing, setEditing] = useState(null) // { id, name, subject, body }
  const [saving, setSaving] = useState(false)

  async function saveEdit() {
    setSaving(true)
    const { error } = await supabase.from('notification_templates')
      .update({ name: editing.name, subject: editing.subject, body: editing.body })
      .eq('id', editing.id)
    setSaving(false)
    if (error) { showToast(error.message, 'error'); return }
    showToast(t('notifications.toastTemplateSaved'), 'success')
    setEditing(null)
    reloadTemplates()
  }

  async function deleteTemplate(id) {
    if (!window.confirm(t('notifications.templateDeleteConfirm'))) return
    const { error } = await supabase.from('notification_templates').delete().eq('id', id)
    if (error) showToast(error.message, 'error')
    else reloadTemplates()
  }

  if (templates.length === 0) {
    return <p className="text-sm text-on-surface-variant">{t('notifications.emptyTemplates')}</p>
  }

  return (
    <div className="overflow-x-auto rounded-2xl border border-outline-variant/60 bg-surface-container-lowest">
      <table className="min-w-full divide-y divide-outline-variant/40">
        <thead className="bg-surface-container-low">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-on-surface-variant">{t('notifications.colName')}</th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-on-surface-variant">{t('notifications.colSubject')}</th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-on-surface-variant">{t('notifications.colActions')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-outline-variant/40">
          {templates.map(tpl => (
            <tr key={tpl.id}>
              <td className="px-4 py-3 text-sm font-medium text-on-surface">{tpl.name}</td>
              <td className="px-4 py-3 text-sm text-on-surface-variant">{tpl.subject}</td>
              <td className="px-4 py-3 text-sm whitespace-nowrap">
                <button onClick={() => onUse(tpl)} className="mr-3 font-semibold text-oasis-700 hover:underline">
                  {t('notifications.templateUseBtn')}
                </button>
                <button onClick={() => setEditing({ id: tpl.id, name: tpl.name, subject: tpl.subject, body: tpl.body })}
                  className="mr-3 text-on-surface-variant hover:underline">
                  {t('notifications.templateEditBtn')}
                </button>
                <button onClick={() => deleteTemplate(tpl.id)} className="text-red-600 hover:underline">
                  {t('notifications.templateDeleteBtn')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#2c241c]/50 p-4"
          onClick={e => { if (e.target === e.currentTarget) setEditing(null) }}>
          <div className="w-full max-w-2xl bg-surface-container-lowest rounded-2xl shadow-xl border border-outline-variant/60 p-6 max-h-[90vh] overflow-y-auto space-y-4">
            <h3 className="font-display text-xl font-semibold tracking-tight text-on-surface">{t('notifications.editTemplateTitle')}</h3>
            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('notifications.colName')}</label>
              <input type="text" value={editing.name}
                onChange={e => setEditing(p => ({ ...p, name: e.target.value }))} className={INPUT_CLS} />
            </div>
            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('notifications.labelSubject')}</label>
              <input type="text" value={editing.subject}
                onChange={e => setEditing(p => ({ ...p, subject: e.target.value }))} className={INPUT_CLS} />
            </div>
            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('notifications.labelBody')}</label>
              <textarea rows={10} value={editing.body}
                onChange={e => setEditing(p => ({ ...p, body: e.target.value }))}
                className="w-full rounded-lg border border-outline-variant px-3 py-2.5 text-sm font-mono focus:border-primary focus:outline-none focus:ring-2 focus:ring-oasis-200 transition" />
            </div>
            <div className="flex gap-3">
              <button onClick={saveEdit} disabled={saving || !editing.name.trim()}
                className="flex-1 rounded-xl bg-primary py-2.5 text-sm font-semibold text-white hover:bg-oasis-800 disabled:opacity-60 transition">
                {t('notifications.saveBtn')}
              </button>
              <button onClick={() => setEditing(null)}
                className="rounded-xl border border-outline-variant px-5 py-2.5 text-sm font-medium text-on-surface-variant hover:bg-surface-container-low transition">
                {t('notifications.closeBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Implemented in Task 11
const NOTIFICATION_STATUS_STYLES = {
  scheduled: 'bg-amber-100 text-amber-800 border-amber-200',
  sending:   'bg-blue-100 text-blue-800 border-blue-200',
  sent:      'bg-green-100 text-green-800 border-green-200',
  failed:    'bg-red-100 text-red-800 border-red-200',
  cancelled: 'bg-surface-container text-on-surface-variant border-outline-variant/60',
}

function audienceSummary(t, filters) {
  const parts = []
  if (filters?.payment_status?.length) parts.push(filters.payment_status.join(', '))
  if (filters?.needs_transport === true) parts.push(t('notifications.transportYes'))
  if (filters?.needs_transport === false) parts.push(t('notifications.transportNo'))
  if (filters?.sex) parts.push(filters.sex)
  if (filters?.min_age != null || filters?.max_age != null) {
    parts.push(`${filters.min_age ?? '0'}–${filters.max_age ?? '∞'}`)
  }
  if (filters?.room_ids?.length) parts.push(`${filters.room_ids.length} ${t('notifications.filterRooms').toLowerCase()}`)
  return parts.length > 0 ? parts.join(' · ') : t('notifications.audienceAll')
}

function HistoryView({ t, notifications, reloadNotifications, showToast }) {
  const [counts, setCounts] = useState({})      // notification_id -> { sent, failed }
  const [expandedId, setExpandedId] = useState(null)
  const [recipients, setRecipients] = useState([])
  const [retrying, setRetrying] = useState(null)

  useEffect(() => {
    const ids = notifications.map(n => n.id)
    if (ids.length === 0) return
    supabase.from('notification_recipients').select('notification_id, status').in('notification_id', ids)
      .then(({ data }) => {
        const next = {}
        for (const r of data ?? []) {
          next[r.notification_id] ??= { sent: 0, failed: 0 }
          next[r.notification_id][r.status]++
        }
        setCounts(next)
      })
  }, [notifications])

  async function toggleExpand(id) {
    if (expandedId === id) { setExpandedId(null); return }
    const { data } = await supabase
      .from('notification_recipients')
      .select('*, registrations(full_name)')
      .eq('notification_id', id)
      .order('status')
    setRecipients(data ?? [])
    setExpandedId(id)
  }

  async function cancelScheduled(n) {
    if (!window.confirm(t('notifications.cancelScheduledConfirm'))) return
    const { error } = await supabase.from('notifications')
      .update({ status: 'cancelled' }).eq('id', n.id).eq('status', 'scheduled')
    if (error) showToast(error.message, 'error')
    else { showToast(t('notifications.toastCancelled'), 'success'); reloadNotifications() }
  }

  async function retryFailed(n) {
    setRetrying(n.id)
    const { data, error } = await supabase.functions.invoke('send-notification', {
      body: { notificationId: n.id, retryFailed: true },
    })
    setRetrying(null)
    if (error || data?.error) showToast(error?.message ?? data.error, 'error')
    else showToast(t('notifications.toastRetryDone'), 'success')
    reloadNotifications()
    if (expandedId === n.id) { setExpandedId(null) }
  }

  if (notifications.length === 0) {
    return <p className="text-sm text-on-surface-variant">{t('notifications.emptyHistory')}</p>
  }

  return (
    <div className="overflow-x-auto rounded-2xl border border-outline-variant/60 bg-surface-container-lowest">
      <table className="min-w-full divide-y divide-outline-variant/40">
        <thead className="bg-surface-container-low">
          <tr>
            {['colDate', 'colSubject', 'colAudience', 'colStatus', 'colResults', 'colActions'].map(k => (
              <th key={k} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-on-surface-variant whitespace-nowrap">
                {t(`notifications.${k}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-outline-variant/40">
          {notifications.map(n => {
            const c = counts[n.id] ?? { sent: 0, failed: 0 }
            return (
              <FragmentRow key={n.id} n={n} c={c} t={t}
                expanded={expandedId === n.id} recipients={recipients}
                onToggle={() => toggleExpand(n.id)}
                onCancel={() => cancelScheduled(n)}
                onRetry={() => retryFailed(n)}
                retrying={retrying === n.id}
              />
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function FragmentRow({ n, c, t, expanded, recipients, onToggle, onCancel, onRetry, retrying }) {
  const canRetry = ['sent', 'failed', 'sending'].includes(n.status) && c.failed > 0
  return (
    <>
      <tr className="cursor-pointer hover:bg-surface-container-low" onClick={onToggle}>
        <td className="px-4 py-3 text-sm text-on-surface-variant whitespace-nowrap">
          {new Date(n.scheduled_at ?? n.created_at).toLocaleString('es-DO')}
        </td>
        <td className="px-4 py-3 text-sm font-medium text-on-surface">{n.subject}
          <span className="block text-xs text-on-surface-variant/70">{n.camps?.name}</span>
        </td>
        <td className="px-4 py-3 text-sm text-on-surface-variant">{audienceSummary(t, n.filters)}</td>
        <td className="px-4 py-3">
          <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${NOTIFICATION_STATUS_STYLES[n.status] ?? ''}`}>
            {t(`notifications.status${n.status.charAt(0).toUpperCase()}${n.status.slice(1)}`)}
          </span>
        </td>
        <td className="px-4 py-3 text-sm text-on-surface-variant whitespace-nowrap">
          {t('notifications.resultsSummary', { sent: c.sent, failed: c.failed })}
        </td>
        <td className="px-4 py-3 text-sm whitespace-nowrap" onClick={e => e.stopPropagation()}>
          {n.status === 'scheduled' && n.scheduled_at && (
            <button onClick={onCancel} className="text-red-600 hover:underline">
              {t('notifications.cancelScheduledBtn')}
            </button>
          )}
          {canRetry && (
            <button onClick={onRetry} disabled={retrying} className="text-oasis-700 font-semibold hover:underline disabled:opacity-50">
              {retrying ? t('notifications.sendingBtn') : t('notifications.retryFailedBtn')}
            </button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} className="bg-surface-container-low px-6 py-3">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase text-on-surface-variant">
                  <th className="py-1 pr-4">{t('notifications.colRecipient')}</th>
                  <th className="py-1 pr-4">{t('notifications.colEmail')}</th>
                  <th className="py-1 pr-4">{t('notifications.colStatus')}</th>
                  <th className="py-1">{t('notifications.colError')}</th>
                </tr>
              </thead>
              <tbody>
                {recipients.map(r => (
                  <tr key={r.id} className="border-t border-outline-variant/60">
                    <td className="py-1.5 pr-4">{r.registrations?.full_name}</td>
                    <td className="py-1.5 pr-4 text-on-surface-variant">{r.email ?? '—'}</td>
                    <td className="py-1.5 pr-4">
                      <span className={r.status === 'sent' ? 'text-green-700' : 'text-red-700'}>
                        {t(`notifications.status${r.status.charAt(0).toUpperCase()}${r.status.slice(1)}`)}
                      </span>
                    </td>
                    <td className="py-1.5 text-red-600">{r.error_message ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  )
}
